import { useMemo, useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import RequestList from './components/RequestList';
import RequestPipeline from './components/RequestPipeline';
import RequestDetail from './components/RequestDetail';
import RequestForm from './components/RequestForm';
import { SERVICES, REGIONS, SOURCES, RESPONSIBLES, REQUEST_STATUSES, VIDEO_STATUSES } from '@/mocks/constants';
import type { QuoteRequest } from '@/types';

type View = 'Liste' | 'Pipeline';

const selectClass =
  'h-10 rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none';

export default function TeklifTalepleri() {
  const { requests } = useAppStore();
  const [view, setView] = useState<View>('Liste');
  const [selected, setSelected] = useState<QuoteRequest | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [service, setService] = useState('');
  const [region, setRegion] = useState('');
  const [source, setSource] = useState('');
  const [status, setStatus] = useState('');
  const [responsible, setResponsible] = useState('');
  const [videoStatus, setVideoStatus] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (q && !r.customerName.toLowerCase().includes(q) && !r.phone.replace(/\s/g, '').includes(q.replace(/\s/g, ''))) {
        return false;
      }
      if (service && r.service !== service) return false;
      if (region && r.region !== region) return false;
      if (source && r.source !== source) return false;
      if (status && r.status !== status) return false;
      if (responsible && r.responsible !== responsible) return false;
      if (videoStatus && r.videoStatus !== videoStatus) return false;
      return true;
    });
  }, [requests, search, service, region, source, status, responsible, videoStatus]);

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
            Operasyon
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Teklif Talepleri</h1>
          <p className="mt-1 text-sm text-foreground-500">Müşteri taleplerini ve video durumunu yönetin.</p>
        </div>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-600"
        >
          <i className="ri-add-line" />
          Yeni Talep
        </button>
      </div>

      {/* Search + view toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          className="h-10 w-full rounded-md border border-background-200 bg-white px-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none sm:w-80"
          placeholder="Müşteri veya telefon ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {(['Liste', 'Pipeline'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                view === v ? 'bg-white text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <select className={selectClass} value={service} onChange={(e) => setService(e.target.value)}>
          <option value="">Tüm Hizmetler</option>
          {SERVICES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className={selectClass} value={region} onChange={(e) => setRegion(e.target.value)}>
          <option value="">Tüm Bölgeler</option>
          {REGIONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select className={selectClass} value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Tüm Kaynaklar</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className={selectClass} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Tüm Durumlar</option>
          {REQUEST_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select className={selectClass} value={responsible} onChange={(e) => setResponsible(e.target.value)}>
          <option value="">Tüm Sorumlular</option>
          {RESPONSIBLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select className={selectClass} value={videoStatus} onChange={(e) => setVideoStatus(e.target.value)}>
          <option value="">Tüm Video Durumları</option>
          {VIDEO_STATUSES.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {view === 'Liste' ? (
        <RequestList requests={filtered} onSelect={setSelected} />
      ) : (
        <RequestPipeline requests={filtered} onSelect={setSelected} />
      )}

      {selected && <RequestDetail request={selected} onClose={() => setSelected(null)} />}
      {formOpen && <RequestForm open={formOpen} onClose={() => setFormOpen(false)} />}
    </div>
  );
}